import type { ContentSpec } from "../contentspec/types.js";
import { splitAccents, TEXT_LIMITS } from "../model/text-limits.js";
import { FAMILIES, progressionIssues, type Family } from "./families.js";
import { numbersOf } from "./numbers.js";
import { COMMON } from "./style.js";
import { normalizeText, sharedWordShare } from "./text.js";

// Pure text helpers live in text.ts (no Node dependency: the web app imports them too).
export { normalizeText, sharedWordShare };

/**
 * Editorial QA V1 (docs/EDITORIAL_CONTRACT_V1.md §10): the deterministic,
 * measurable checks, run on an EditorialPlan or on a ContentSpec.
 *
 * Every issue has a code and the level the contract gives it: `warning`
 * (surfaced to the reviewer, never blocks) or `blocking`. `editorial` is
 * "pass" only when no blocking issue is found; it is the value ContentSpec
 * `qa.editorial` may take. Publish Readiness adds the blocking issues of the
 * ContentSpec to its `qa` gate.
 *
 * Checks that need data a document does not carry are skipped for it:
 * - slide roles (ROLE_ORDER, HOOK_UNPAID, SLIDE_WITHOUT_CLAIM) only run when
 *   the slides declare roles, so ContentSpecs written before roles existed
 *   are not blocked for missing them;
 * - CTA action, angle and claim usage (CTA_OFF_RECOMMENDATION, ANGLE_GENERIC,
 *   UNUSED_KEY_CLAIM) only exist in the EditorialPlan.
 *
 * Profiles (chosen explicitly by the caller, never inferred from the
 * document): `renderer` (default) checks a carousel the renderer will lay out,
 * limits of its layout included; `manual` checks a carousel made by hand, whose
 * text is already printed on the images. In `manual`, only the constraints that
 * belong to the renderer are not applicable (MANUAL_NOT_APPLICABLE); every
 * editorial check runs as in `renderer`. Results of the default profile are
 * unchanged, byte for byte.
 *
 * Pure: same input, same issues, same order. No clock, network or files.
 */
export const EDITORIAL_CHECKS_VERSION = "editorial-qa-v1";

export type EditorialQaProfile = "renderer" | "manual";

export interface EditorialQaOptions {
  /** Default `renderer`. The manual carousel registry always passes `manual`. */
  profile?: EditorialQaProfile;
}

/** A renderer constraint that does not apply to a carousel made by hand, and why. */
export interface NotApplicableRule {
  code: string;
  scope: string;
  reason: string;
}

/**
 * Renderer constraints not applicable in the `manual` profile, decided rule by
 * rule (docs/CAROUSEL_REGISTRY.md §QA). Everything not listed here applies.
 */
export const MANUAL_NOT_APPLICABLE: readonly NotApplicableRule[] = [
  {
    code: "HOOK_TOO_LONG",
    scope: "blocking level only (cover title over the renderer's 80-character layout limit)",
    reason: "the limit is the space of the renderer's cover; the editorial target (≤ 60 characters, ≤ 10 words) still applies as a warning",
  },
  {
    code: "TEXT_TOO_LONG",
    scope: "blocking level only (title over 90, subtitle over 220 characters)",
    reason: "the limits are the space of the renderer's slides; the editorial targets (title ≤ 60, subtitle ≤ 120 characters) still apply as warnings",
  },
  { code: "CTA_LABEL_TOO_LONG", scope: "whole rule", reason: "the 28-character limit is the width of the button the renderer draws" },
  { code: "ACCENT_NOT_IN_TITLE", scope: "whole rule", reason: "accent is an instruction to the renderer (gold block); a manual carousel has none" },
  { code: "TOO_MANY_IMPACT", scope: "whole rule", reason: "impact is a renderer layout variant; a manual carousel has none" },
  {
    code: "IMAGE_ONLY_NOT_ALLOWED",
    scope: "whole rule",
    reason: "image_only and split are renderer instructions; the editorial principle (a slide that prints no text states no fact) is checked on the transcription by the registry (NO_TEXT_SLIDE_WITH_CLAIM)",
  },
  { code: "FIGURE_REPEATED", scope: "whole rule", reason: "figure is a value the renderer draws above the title; a manual carousel has no such field" },
  {
    code: "FAMILY_PROGRESSION",
    scope: "composition checks (list and ranking markers and values, story timeline strip, comparison split panels)",
    reason: "they measure fields the renderer composes; the narrative checks (story reveal, from slide roles) still apply",
  },
];

export type EditorialLevel = "warning" | "blocking";

export interface EditorialIssue {
  code: string;
  level: EditorialLevel;
  message: string;
  field: string | null;
}

export interface EditorialQaResult {
  checks_version: typeof EDITORIAL_CHECKS_VERSION;
  target: "content_spec" | "editorial_plan";
  editorial: "pass" | "fail";
  issues: EditorialIssue[];
  /** Present only in the `manual` profile. */
  profile?: "manual";
  /** Present only in the `manual` profile: the renderer constraints that were not applied. */
  not_applicable?: NotApplicableRule[];
}

/** Editorial targets that are not renderer limits (§4, §6, §9). */
export const EDITORIAL_LIMITS = {
  // calibrated on the published library (docs/EDITORIAL_CALIBRATION.md): covers 8 to about 70 characters, 2–13 words
  hookChars: COMMON.hookChars,
  hookWords: COMMON.hookWords,
  hookBlockingChars: TEXT_LIMITS.coverTitle,
  titleChars: 60,
  // published explanations reach 188 characters (lists, stories)
  subtitleChars: COMMON.subChars,
  titleBlockingChars: TEXT_LIMITS.title,
  subtitleBlockingChars: TEXT_LIMITS.subtitle,
  ctaLabelChars: TEXT_LIMITS.ctaButton,
  maxImpactSlides: 2,
  duplicateShare: 0.8,
} as const;

/**
 * Forbidden hook framings (§4, RESEARCH_CREATE.md §2): empty clickbait
 * formulas, not strong or provocative hooks. Matched as whole words after
 * normalisation (case, accents and punctuation ignored), so "fou" or
 * "incroyablement" alone never match. Kept short and conservative on purpose.
 */
export const FORBIDDEN_FRAMINGS_EN = ["you won't believe", "crazy fact", "went viral"] as const;
export const FORBIDDEN_FRAMINGS_FR = [
  // « vous n'allez pas croire » and its variants
  "vous n'allez pas croire",
  "vous n'allez pas le croire",
  "vous n'allez jamais croire",
  "vous n'allez jamais le croire",
  "vous ne croirez pas",
  "vous ne croirez jamais",
  "tu ne vas pas croire",
  "tu ne vas pas le croire",
  "tu ne vas jamais croire",
  "tu ne vas jamais le croire",
  "tu vas pas croire",
  "tu vas pas le croire",
  "tu ne croiras pas",
  "tu ne croiras jamais",
  // « fait incroyable », « fait complètement fou »
  "fait incroyable",
  "faits incroyables",
  "fait complètement fou",
  "faits complètement fous",
  "fait totalement fou",
  "faits totalement fous",
  // « est devenu viral » as an empty framing
  "devenu viral",
  "devenue virale",
  "devenus viraux",
  "devenues virales",
] as const;
export const FORBIDDEN_FRAMINGS = [...FORBIDDEN_FRAMINGS_EN, ...FORBIDDEN_FRAMINGS_FR] as const;

/** The forbidden framings a text uses (whole words, case, accents and punctuation ignored). */
export function forbiddenFramingsIn(text: string): string[] {
  const normalized = ` ${normalizeText(text)} `;
  return FORBIDDEN_FRAMINGS.filter((framing) => normalized.includes(` ${normalizeText(framing)} `));
}

/** §9: the CTA action recommended for each objective. */
export const CTA_RECOMMENDATION: Record<string, readonly string[]> = {
  non_follower_growth: ["follow"],
  profile_to_follow: ["follow"],
  shares: ["send"],
  retention: ["save"],
  community: ["comment"],
  monetization: ["save", "follow"],
};

/** Roles whose slide must state at least one claim (§6). */
const FACT_ROLES = ["reveal", "proof", "escalation", "contrast", "payoff"];

interface Slide {
  position: number;
  index: number;
  role?: string;
  type: string;
  title: string;
  subtitle?: string;
  accent?: string;
  claim_ids?: string[];
  /** Shows or compares aesthetics and states no fact. */
  illustrative?: boolean;
  image_only?: boolean;
  variant?: string;
  kicker?: string;
  marker?: { value: string; style: string };
  figure?: { value: string; unit?: string; label?: string };
  panels?: Array<{ label: string; figure?: { value: string; unit?: string; label?: string } }>;
  timeline?: { steps: string[]; current: number };
}

interface Claim {
  id?: string;
  text?: string;
  status: string;
  usage?: string;
}

/** What the checks read, from either document. */
interface EditorialInput {
  target: EditorialQaResult["target"];
  objective: string;
  slides: Slide[];
  claims: Claim[];
  ctaLabel: string;
  ctaLabelField: string;
  ctaAction?: string;
  distinctFrom?: string;
  /** Editorial family (docs/EDITORIAL_FAMILIES_V1.md); progression checks run only when set. */
  family?: Family;
  /** Subject / topic: its numbers may frame the hook. */
  subject: string;
}

/** Lowercase, no accents, no punctuation, single spaces. */
/**
 * Words of a hook as a reader counts them: an amount written with thousands separators
 * (« 100 000 € ») is one word, and tokens without a letter or digit (« : », « € », « — ») are not words.
 */
export function hookWordCount(title: string): number {
  const joined = title.replace(/(\d)[\s\u00a0\u202f](?=\d{3}(?!\d))/g, "$1");
  return joined.trim().split(/\s+/).filter((token) => /[\p{L}\p{N}]/u.test(token)).length;
}

const quote = (text: string) => `"${text.length > 50 ? `${text.slice(0, 47)}…` : text}"`;

function toSlides(raw: unknown[] | undefined): Slide[] {
  return (raw ?? []).map((slide, position) => ({ ...(slide as Omit<Slide, "position">), position }));
}

export function editorialInputFromContentSpec(spec: ContentSpec): EditorialInput {
  return {
    target: "content_spec",
    objective: spec.objective,
    slides: toSlides(spec.slides),
    claims: spec.claims as Claim[],
    ctaLabel: spec.cta,
    ctaLabelField: "cta",
    ...(spec.family && (FAMILIES as readonly string[]).includes(spec.family) ? { family: spec.family } : {}),
    subject: spec.topic,
  };
}

/** EditorialPlan (packages/schemas/editorial-plan.schema.json), already schema-valid. */
export interface EditorialPlan {
  input: { objective: string; subject: string };
  family?: { id: Family; source: string; reason: string };
  claims: Claim[];
  angle: { distinct_from: string };
  slides: unknown[];
  cta: { action: string; label: string };
  [key: string]: unknown;
}

export function editorialInputFromPlan(plan: EditorialPlan): EditorialInput {
  return {
    target: "editorial_plan",
    objective: plan.input.objective,
    slides: toSlides(plan.slides),
    claims: plan.claims,
    ctaLabel: plan.cta.label,
    ctaLabelField: "cta.label",
    ctaAction: plan.cta.action,
    distinctFrom: plan.angle.distinct_from,
    ...(plan.family ? { family: plan.family.id } : {}),
    subject: plan.input.subject,
  };
}

export function evaluateEditorialQa(input: EditorialInput, options: EditorialQaOptions = {}): EditorialQaResult {
  const manual = options.profile === "manual";
  const issues: EditorialIssue[] = [];
  const add = (code: string, level: EditorialLevel, message: string, field: string | null = null) =>
    issues.push({ code, level, message, field });
  const slides = [...input.slides].sort((a, b) => a.index - b.index);
  const at = (slide: Slide, key?: string) => `slides[${slide.position}]${key ? `.${key}` : ""}`;
  const label = (slide: Slide) => `slide ${slide.index}${slide.role ? ` (${slide.role})` : ""}`;
  const claims = new Map(input.claims.filter((c) => c.id !== undefined).map((c) => [c.id!, c]));
  const withRoles = slides.filter((slide) => slide.role !== undefined);

  // ---- hook (§4) -------------------------------------------------------------
  const cover = slides.find((slide) => slide.type === "cover");
  if (cover) {
    const chars = cover.title.length;
    const count = hookWordCount(cover.title);
    if (!manual && chars > EDITORIAL_LIMITS.hookBlockingChars) {
      add("HOOK_TOO_LONG", "blocking", `cover title is ${chars} characters (hard limit ${EDITORIAL_LIMITS.hookBlockingChars})`, at(cover, "title"));
    } else if (chars > EDITORIAL_LIMITS.hookChars || count > EDITORIAL_LIMITS.hookWords) {
      add(
        "HOOK_TOO_LONG",
        "warning",
        `cover title is ${chars} characters and ${count} words (target ≤ ${EDITORIAL_LIMITS.hookChars} characters, ≤ ${EDITORIAL_LIMITS.hookWords} words)`,
        at(cover, "title"),
      );
    }
    for (const framing of forbiddenFramingsIn(cover.title)) {
      add("HOOK_FORBIDDEN_FRAMING", "blocking", `cover title uses the forbidden framing "${framing}"`, at(cover, "title"));
    }
  }

  // An image-only slide prints no text: only a split slide that states no fact may be one (§6).
  for (const slide of slides.filter((s) => !manual && s.image_only)) {
    if (slide.variant !== "split" || !slide.illustrative) {
      const why = [slide.variant !== "split" ? "it is not a split slide" : "", !slide.illustrative ? "it is not marked illustrative (its facts would not be printed)" : ""].filter(Boolean).join(" and ");
      add("IMAGE_ONLY_NOT_ALLOWED", "blocking", `${label(slide)} is marked image_only but ${why}`, at(slide, "image_only"));
    }
  }

  // ---- roles (§5) ------------------------------------------------------------
  if (withRoles.length) {
    const payoffs = withRoles.filter((slide) => slide.role === "payoff");
    if (!payoffs.length) {
      add("HOOK_UNPAID", "blocking", "no payoff slide: the promise of the hook is never resolved");
    }
    for (const payoff of payoffs) {
      const unverified = (payoff.claim_ids ?? []).filter((id) => claims.get(id)?.status !== "verified");
      if (unverified.length) {
        add("HOOK_UNPAID", "blocking", `${label(payoff)} rests on claims that are not verified: ${unverified.join(", ")}`, at(payoff, "claim_ids"));
      }
    }

    const hooks = withRoles.filter((slide) => slide.role === "hook");
    const ctas = withRoles.filter((slide) => slide.role === "cta");
    const first = slides[0]!;
    const last = slides[slides.length - 1]!;
    if (first.role !== "hook") add("ROLE_ORDER", "blocking", `the first slide must be the hook (${label(first)})`, at(first, "role"));
    if (hooks.length > 1) add("ROLE_ORDER", "blocking", `${hooks.length} hook slides; there must be exactly one`);
    if (ctas.length > 1) add("ROLE_ORDER", "blocking", `${ctas.length} cta slides; there can be at most one`);
    for (const cta of ctas) {
      if (cta !== last) add("ROLE_ORDER", "blocking", `${label(cta)} must be the last slide`, at(cta, "role"));
    }
    const firstPayoff = payoffs[0];
    const lateReveals = firstPayoff ? withRoles.filter((s) => s.role === "reveal" && s.index > firstPayoff.index) : [];
    for (const reveal of lateReveals) {
      add("ROLE_ORDER", "blocking", `${label(reveal)} comes after the payoff (slide ${firstPayoff!.index})`, at(reveal, "role"));
    }

    for (const slide of withRoles) {
      // An illustrative slide compares aesthetics and states no fact: it is exempt from the claim
      // requirement of its role, and may not cite a claim (it would then be a factual slide).
      if (slide.illustrative) {
        if ((slide.claim_ids ?? []).length) {
          add("ILLUSTRATIVE_WITH_CLAIM", "blocking", `${label(slide)} is marked illustrative but cites claims (${slide.claim_ids!.join(", ")}); a slide either states facts or compares aesthetics`, at(slide, "illustrative"));
        }
      } else if (FACT_ROLES.includes(slide.role!) && !(slide.claim_ids ?? []).length) {
        add("SLIDE_WITHOUT_CLAIM", "blocking", `${label(slide)} states no claim (claim_ids is empty)`, at(slide, "claim_ids"));
      }
    }

    // Every number shown on a slide comes from a claim it cites (CLAUDE.md: no unverified factual claims).
    // Hook and cta may frame with the numbers of any usable claim, the subject and the count of fact
    // slides; the timeline recaps the dates of the whole carousel; the marker is structural.
    const usable = input.claims.filter((c) => c.status === "verified" && (c.usage === undefined || c.usage === "use"));
    const allClaimNumbers = usable.flatMap((c) => numbersOf(c.text ?? ""));
    const factCount = withRoles.filter((s) => (s.claim_ids ?? []).length > 0).length;
    const framing = new Set([...allClaimNumbers, ...numbersOf(input.subject), String(factCount)]);
    for (const slide of withRoles) {
      const allowed = new Set(slide.role === "hook" || slide.role === "cta" ? framing : numbersOf(input.subject));
      for (const id of slide.claim_ids ?? []) for (const n of numbersOf(claims.get(id)?.text ?? "")) allowed.add(n);
      if (slide.marker) allowed.add(slide.marker.value);
      const shown: Array<[string, string | undefined]> = [
        ["title", slide.title],
        ["subtitle", slide.subtitle],
        ["kicker", slide.kicker],
        // fields are checked one by one: joined with spaces, "2022" + "330" would read as 2022330
        ["figure", slide.figure?.value],
        ["figure", slide.figure?.label],
        ...(slide.panels ?? []).flatMap((p, i): Array<[string, string | undefined]> => [
          [`panels[${i}]`, p.label],
          [`panels[${i}]`, p.figure?.value],
          [`panels[${i}]`, p.figure?.label],
        ]),
      ];
      for (const [key, text] of shown) {
        const invented = numbersOf(text ?? "").filter((n) => !allowed.has(n));
        if (invented.length) {
          add("UNSOURCED_NUMBER", "blocking", `${label(slide)}: ${key} shows ${invented.join(", ")}, found in no claim the slide cites`, at(slide, key));
        }
      }
      const timelineNumbers = new Set([...allClaimNumbers, ...allowed]);
      const offTimeline = (slide.timeline?.steps ?? []).flatMap(numbersOf).filter((n) => !timelineNumbers.has(n));
      if (offTimeline.length) {
        add("UNSOURCED_NUMBER", "blocking", `${label(slide)}: timeline shows ${[...new Set(offTimeline)].join(", ")}, found in no usable claim`, at(slide, "timeline"));
      }
    }
  }

  // ---- family progression (docs/EDITORIAL_FAMILIES_V1.md) ---------------------
  // Manual: only the narrative checks, and only when the slides declare roles (as ROLE_ORDER).
  if (input.family && (!manual || withRoles.length)) {
    for (const issue of progressionIssues(input.family, slides, cover?.title ?? "", manual ? "narrative" : "all")) {
      const slide = issue.slide ? slides.find((s) => s.index === issue.slide!.index) : undefined;
      add("FAMILY_PROGRESSION", issue.level, `${input.family}: ${issue.message}`, slide ? at(slide) : "family");
    }
  }

  // ---- claims on slides (§2, §6) ---------------------------------------------
  const stated = new Set<string>();
  for (const slide of slides) {
    const ids = slide.claim_ids ?? [];
    for (const id of ids) {
      const claim = claims.get(id);
      const why = !claim
        ? "is not a claim of this document"
        : claim.status !== "verified"
          ? `is "${claim.status}"`
          : claim.usage !== undefined && claim.usage !== "use"
            ? `has usage "${claim.usage}"`
            : null;
      if (why) add("CLAIM_NOT_USABLE", "blocking", `${label(slide)} cites claim ${id}, which ${why}; only verified claims marked use may appear on a slide`, at(slide, "claim_ids"));
    }
    if (ids.length && ids.every((id) => stated.has(id))) {
      add("REPEATED_CLAIMS", "warning", `${label(slide)} only states claims already stated earlier (${ids.join(", ")})`, at(slide, "claim_ids"));
    }
    ids.forEach((id) => stated.add(id));
  }

  // ---- text (§6) -------------------------------------------------------------
  for (const key of ["title", "subtitle"] as const) {
    const texts = slides.filter((slide) => (slide[key] ?? "").trim());
    for (let i = 0; i < texts.length; i += 1) {
      for (let j = i + 1; j < texts.length; j += 1) {
        const share = sharedWordShare(texts[i]![key]!, texts[j]![key]!);
        if (share >= EDITORIAL_LIMITS.duplicateShare) {
          add(
            "DUPLICATE_TEXT",
            "warning",
            `${label(texts[i]!)} and ${label(texts[j]!)} have near-identical ${key}s (${Math.round(share * 100)} % shared words)`,
            at(texts[j]!, key),
          );
        }
      }
    }
  }
  // the key figure is drawn big above the title: the same number in the title shows it twice (Golden Test V1.1)
  for (const slide of manual ? [] : slides) {
    const figure = numbersOf(slide.figure?.value ?? "");
    const repeated = figure.filter((n) => numbersOf(slide.title).includes(n));
    if (repeated.length) {
      add("FIGURE_REPEATED", "warning", `${label(slide)}: the figure ${slide.figure!.value} is drawn above the title and repeated in it; keep the number in one place`, at(slide, "title"));
    }
  }
  for (const slide of slides) {
    if (slide.type !== "cover") {
      const chars = slide.title.length;
      if (!manual && chars > EDITORIAL_LIMITS.titleBlockingChars) {
        add("TEXT_TOO_LONG", "blocking", `${label(slide)}: title is ${chars} characters (renderer limit ${EDITORIAL_LIMITS.titleBlockingChars})`, at(slide, "title"));
      } else if (chars > EDITORIAL_LIMITS.titleChars) {
        add("TEXT_TOO_LONG", "warning", `${label(slide)}: title is ${chars} characters (target ≤ ${EDITORIAL_LIMITS.titleChars})`, at(slide, "title"));
      }
    }
    const subtitle = slide.subtitle ?? "";
    if (!manual && subtitle.length > EDITORIAL_LIMITS.subtitleBlockingChars) {
      add("TEXT_TOO_LONG", "blocking", `${label(slide)}: subtitle is ${subtitle.length} characters (renderer limit ${EDITORIAL_LIMITS.subtitleBlockingChars})`, at(slide, "subtitle"));
    } else if (subtitle.length > EDITORIAL_LIMITS.subtitleChars) {
      add("TEXT_TOO_LONG", "warning", `${label(slide)}: subtitle is ${subtitle.length} characters (target ≤ ${EDITORIAL_LIMITS.subtitleChars})`, at(slide, "subtitle"));
    }
    for (const accent of manual ? [] : splitAccents(slide.accent)) {
      // Same match as the renderer's accent highlight (model/slides.ts).
      if (!slide.title.toLocaleLowerCase("fr").includes(accent.toLocaleLowerCase("fr"))) {
        add("ACCENT_NOT_IN_TITLE", "blocking", `${label(slide)}: accent "${accent}" is not in the title`, at(slide, "accent"));
      }
    }
  }

  // ---- CTA (§9) --------------------------------------------------------------
  if (!manual && input.ctaLabel.length > EDITORIAL_LIMITS.ctaLabelChars) {
    add(
      "CTA_LABEL_TOO_LONG",
      "blocking",
      `CTA label ${quote(input.ctaLabel)} is ${input.ctaLabel.length} characters (limit ${EDITORIAL_LIMITS.ctaLabelChars}; the renderer would show no button)`,
      input.ctaLabelField,
    );
  }
  const recommended = CTA_RECOMMENDATION[input.objective];
  if (input.ctaAction !== undefined && recommended && !recommended.includes(input.ctaAction)) {
    add(
      "CTA_OFF_RECOMMENDATION",
      "warning",
      `CTA action "${input.ctaAction}" differs from the recommendation for "${input.objective}" (${recommended.join(" or ")}); accepted when it serves the content better`,
      "cta.action",
    );
  }

  // ---- layout, angle, claim coverage ------------------------------------------
  const impacts = slides.filter((slide) => slide.variant === "impact");
  if (!manual && impacts.length > EDITORIAL_LIMITS.maxImpactSlides) {
    add("TOO_MANY_IMPACT", "warning", `${impacts.length} impact slides (at most ${EDITORIAL_LIMITS.maxImpactSlides} so it stays strong)`);
  }
  if (input.distinctFrom !== undefined && !input.distinctFrom.trim()) {
    add("ANGLE_GENERIC", "blocking", "angle.distinct_from is empty: the angle cannot be told apart from a generic summary", "angle.distinct_from");
  }
  if (input.target === "editorial_plan") {
    input.claims.forEach((claim, index) => {
      if (claim.usage === "use" && claim.id !== undefined && !stated.has(claim.id)) {
        add("UNUSED_KEY_CLAIM", "warning", `claim ${claim.id} is marked use but appears on no slide`, `claims[${index}]`);
      }
    });
  }

  return {
    checks_version: EDITORIAL_CHECKS_VERSION,
    target: input.target,
    editorial: issues.some((issue) => issue.level === "blocking") ? "fail" : "pass",
    issues,
    ...(manual ? { profile: "manual" as const, not_applicable: MANUAL_NOT_APPLICABLE.map((rule) => ({ ...rule })) } : {}),
  };
}

export const checkContentSpecEditorial = (spec: ContentSpec, options: EditorialQaOptions = {}) => evaluateEditorialQa(editorialInputFromContentSpec(spec), options);
export const checkEditorialPlan = (plan: EditorialPlan, options: EditorialQaOptions = {}) => evaluateEditorialQa(editorialInputFromPlan(plan), options);

export function formatEditorialIssue(issue: EditorialIssue): string {
  return `[${issue.level}] ${issue.code}: ${issue.message}${issue.field ? ` (${issue.field})` : ""}`;
}
